import { useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Link2, LayoutGrid, BarChart3, CheckCircle, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { db } from '@/services/firebase';
import { Button, Input } from '@/components/ui';

const WaitingList = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('waitlist.invalidEmail'));
      return;
    }

    setIsLoading(true);
    try {
      const docRef = doc(db, 'waitlist', email.toLowerCase());
      const existing = await getDoc(docRef);
      if (existing.exists()) {
        setError(t('waitlist.alreadyRegistered'));
        return;
      }

      await setDoc(docRef, {
        email: email.toLowerCase(),
        createdAt: serverTimestamp(),
      });

      setIsSuccess(true);
    } catch {
      setError(t('waitlist.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: Link2, title: t('waitlist.feature1Title'), description: t('waitlist.feature1Description') },
    { icon: LayoutGrid, title: t('waitlist.feature2Title'), description: t('waitlist.feature2Description') },
    { icon: BarChart3, title: t('waitlist.feature3Title'), description: t('waitlist.feature3Description') },
  ];

  return (
    <section id="waitlist" className="py-24 bg-gray-50 dark:bg-dark-900 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-10 w-72 h-72 bg-lime-200 dark:bg-lime-900 rounded-full opacity-30 dark:opacity-10 blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-lavender-200 dark:bg-lavender-900 rounded-full opacity-30 dark:opacity-10 blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-lime-100 dark:bg-lime-900/30 rounded-full mb-6">
            <span className="text-sm font-medium text-lime-700 dark:text-lime-400">
              {t('waitlist.comingSoon')}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-dark-800 dark:text-white mb-4">
            {t('waitlist.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('waitlist.subtitle')}
          </p>
        </div>

        {isSuccess ? (
          <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-elevated dark:shadow-none dark:border dark:border-dark-700 p-8 max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-lime-100 dark:bg-lime-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-lime-600 dark:text-lime-400" />
            </div>
            <h3 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">
              {t('waitlist.successTitle')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('waitlist.successMessage')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-lg mx-auto mb-16">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('waitlist.emailPlaceholder')}
                  required
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                {t('waitlist.joinButton')}
              </Button>
            </div>
            {error && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 text-center">
              {t('waitlist.noSpam')}
            </p>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white dark:bg-dark-800 rounded-2xl p-6 shadow-sm dark:shadow-none dark:border dark:border-dark-700"
            >
              <div className="w-12 h-12 bg-lime-100 dark:bg-lime-900/30 rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-lime-600 dark:text-lime-400" />
              </div>
              <h3 className="font-semibold text-dark-800 dark:text-white mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WaitingList;
